/**
 * <cw-order-again>: Order Again (snippets/cw-order-again.liquid;
 * design-handoff/specs/order-again-spec.md, components/order-again.md; Shopping v13 1j =
 * ReorderScreen3 + reorder3(), ported).
 *
 * Phones: a screen over the page, under the basket bar and tabs (non-modal: the tabs stay
 * live and Order Again is the one lit); 600–1023: a 420 panel with its own basket bar;
 * 1024+: a 420 panel. Signed out, 'cw:orderagain:open' never reaches here: cw-signin.js asks
 * for sign-in first.
 *
 * Top to bottom: the essentials shelf (its bubble scrolls to that aisle's card or tile here
 * and flashes it), a strip when the chosen address is past the delivery edge, the
 * new-customer card, the due shortcut, the customer's products by aisle ("Your usuals"), one
 * product for each aisle they have not shopped, their past orders.
 *
 * Rules (spec §3–7):
 * - Rows come from real orders only (#cw-history): one per line of the newest order that had
 *   the product, so two grinds are two rows. Nothing is pre-ticked. The tick adds that pack at
 *   that order's quantity, and takes the line out again; ± change it, − at 1 takes it out.
 * - A row follows the basket: when its pack is not in the basket but one other pack of the
 *   product is (a substitute, or a pack changed in the drawer), the row shows that one.
 * - The size line opens the options drawer at the row's pack. With two or more packs of the
 *   product in the basket, ± open the drawer instead (the category rule).
 * - Reasons, one per row: "Likely running low" / "Due now" (olive), "Due in 2 weeks",
 *   "Last bought 29 Aug" (grey), "500 ml sold out" / "Sold out" (amber). The owner's word is
 *   "Sold out" (1 Oct): nothing knows when stock returns, so no date is promised. A sold-out
 *   usual pack with other sizes on sale opens the drawer at the nearest one.
 * - No kitchen-pair suggestion rows: the owner decides those later (1 Oct).
 * - Tiles: one product per aisle the customer has neither in the basket nor among their
 *   products (Theme settings → Order Again), the squirrel's aisle first, at most 4; all 9 as
 *   "Start with the basics" before a first order. Their order holds while the screen is open,
 *   and an added tile keeps its place with a stepper.
 * - REORDER puts every line still on sale in the basket, never lowering what is there; then
 *   it reads "In basket ✓" and opens the basket. Lines made to the customer's own recipe are
 *   not added (that would drop the recipe).
 * - No totals on this screen: row prices only (spec §2).
 */
import { CwSheet, screenUp, screenDown } from '@cw/sheet';
import { cart, MAX_PER_LINE } from '@cw/cart';
import { AISLES, learn, aisleOf } from '@cw/aisles';
import { esc, rupees, configOf, defaultPack, productData } from '@cw/products';
import { setSel } from '@cw/picker';
import { usuals, dueItems, namesOf, addDue, shortName } from '@cw/usuals';
import { view } from '@cw/delivery';

const PHONE = window.matchMedia('(max-width: 599.98px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

const read = (id) => {
  try {
    return JSON.parse(document.getElementById(id)?.textContent || 'null');
  } catch {
    return null;
  }
};
const load = (key, fallback) => {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null') ?? fallback;
  } catch {
    return fallback;
  }
};
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const lowerFirst = (text) => (text ? text[0].toLowerCase() + text.slice(1) : '');
/** "29 Aug", "14 Sept", as the design writes dates. */
function dayMonth(seconds) {
  const d = new Date(seconds * 1000);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
/** A usual not due yet: "Due tomorrow", "Due in 5 days", "Due in 2 weeks". */
function dueIn(days) {
  if (days < 1.5) return 'Due tomorrow';
  if (days < 7) return `Due in ${Math.round(days)} days`;
  const weeks = Math.round(days / 7);
  return `Due in ${weeks} week${weeks === 1 ? '' : 's'}`;
}

/** The curated words for a tile (data/basket-reasons.json aisle_picks), when the pick is that product. */
const PICK_WHY = Object.values(read('cw-basket-reasons')?.aisle_picks || {});
const whyOf = (title) => (PICK_WHY.find(([name]) => title === name || title.startsWith(name)) || [])[1] || '';

/**
 * The pack offered when the usual one is sold out (spec §7.2): the next size up in the same
 * grind and form, else the next one down, else the nearest size of anything on sale.
 */
function nearest(m, usual) {
  const same = m.variants
    .filter((v) => v.form === usual.form && v.grind === usual.grind)
    .sort((a, b) => a.size - b.size);
  const at = same.findIndex((v) => v.id === usual.id);
  for (let step = 1; at >= 0 && step < same.length; step += 1) {
    for (const j of [at + step, at - step]) if (same[j]?.available) return same[j];
  }
  return m.variants.filter((v) => v.available).sort((a, b) => Math.abs(a.size - usual.size) - Math.abs(b.size - usual.size))[0] || null;
}

const offline = () => {
  if (navigator.onLine !== false) return false;
  document.dispatchEvent(new CustomEvent('cw:offline'));
  return true;
};

class CwOrderAgain extends CwSheet {
  #rows = [];
  #tiles = [];
  #orders = [];
  /** The due shortcut's last ADD: {variants, run}, until UNDO or one of them leaves the basket. */
  #added = null;
  #flashTimer = 0;
  #icons = {};
  /** Phones: what scrolling has folded here, and what the page under it had folded. */
  #fold = { folded: false, navHidden: false };
  #pageFold = { folded: false, navHidden: false };
  #last = 0;
  #acc = 0;

  connectedCallback() {
    super.connectedCallback();
    this.bodyEl = this.querySelector('[data-cw="body"]');
    this.whereEl = this.querySelector('[data-cw="where"]');
    const icons = this.querySelector('template[data-cw="icons"]');
    for (const el of icons?.content.querySelectorAll('[data-icon]') || []) this.#icons[el.dataset.icon] = el.innerHTML.trim();
    document.addEventListener('cw:orderagain:open', this.#onRequest);
    document.addEventListener('cw:cart:change', this.#onCart);
    document.addEventListener('cw:delivery:change', this.#paintWhere);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('cw:shelf:go', this.#onShelfGo);
    this.addEventListener('cw:shelf:render', this.#onShelf);
    this.bodyEl.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('cw:fold', this.#onPageFold);
    const measure = () => {
      const head = this.querySelector('.cw-oa__head');
      if (head?.scrollHeight) this.style.setProperty('--cw-oa-head-h', `${head.scrollHeight}px`);
    };
    document.fonts?.ready.then(measure);
    this.measureHead = measure;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cw:orderagain:open', this.#onRequest);
    document.removeEventListener('cw:cart:change', this.#onCart);
    document.removeEventListener('cw:delivery:change', this.#paintWhere);
    document.removeEventListener('cw:fold', this.#onPageFold);
  }

  /** The page's own fold (the aisle's), kept to give back when this screen goes. */
  #onPageFold = (event) => {
    if (event.detail?.source === 'oa') return;
    this.#pageFold = { folded: !!event.detail?.folded, navHidden: !!event.detail?.navHidden };
  };

  /**
   * Phones: the list folds the head and the shelf title and hides the tabs as it scrolls
   * down, as the aisle does (owner, 1 Oct); any scroll up brings the tabs back, and the top
   * unfolds everything. Announced as 'cw:fold' {source: 'oa'} for the tabs and the bar.
   */
  #onScroll = () => {
    if (!PHONE.matches || !this.isOpen) return;
    const y = this.bodyEl.scrollTop;
    const last = this.#last;
    if (y === last) return;
    this.#last = y;
    if (y <= 4) {
      this.#acc = 0;
      this.#setFold({ folded: false, navHidden: false });
    } else {
      this.#acc = y > last ? this.#acc + (y - last) : 0;
      if (y > 40 && this.#acc >= 40) this.#setFold({ folded: true, navHidden: true });
      else if (y < last - 3) this.#setFold({ navHidden: false });
    }
  };

  #setFold(patch, announce = true) {
    const next = { ...this.#fold, ...patch };
    if (next.folded === this.#fold.folded && next.navHidden === this.#fold.navHidden) return;
    this.#fold = next;
    this.panel.classList.toggle('is-folded', next.folded);
    this.dialog.classList.toggle('is-nav-hidden', next.navHidden);
    this.querySelector('cw-shelf')?.toggleAttribute('data-folded', next.folded);
    if (announce) document.dispatchEvent(new CustomEvent('cw:fold', { detail: { ...next, source: 'oa' } }));
  }

  /** Phones: a screen under the tabs. 600+: a panel. */
  modal() {
    return !PHONE.matches;
  }

  /** A screen, not a sheet: no swipe-down to close. */
  swipeable() {
    return false;
  }

  #onRequest = (event) => {
    event.preventDefault();
    if (!this.isOpen) {
      this.#build();
      this.bodyEl.scrollTop = 0;
    }
    this.#paint();
    this.#paintWhere();
    this.open(event.detail?.source);
    if (!this.modal()) {
      screenUp(this, 'orderagain');
      // Opens unfolded, with the tabs showing.
      this.measureHead?.();
      this.#last = this.bodyEl.scrollTop;
      this.#acc = 0;
      this.#setFold({ folded: false, navHidden: false }, false);
      document.dispatchEvent(new CustomEvent('cw:fold', { detail: { folded: false, navHidden: false, source: 'oa' } }));
    }
  };

  onClosed() {
    screenDown(this);
    // Back on the page: its header as it was, the tabs showing (a tab was just tapped), and the
    // page told to take that as its own state ('cw:fold:sync', cw-aisle.js / cw-home.js).
    if (this.#fold.folded || this.#fold.navHidden) this.#setFold({ folded: false, navHidden: false }, false);
    document.dispatchEvent(new CustomEvent('cw:fold', { detail: { folded: this.#pageFold.folded, navHidden: false, source: 'page' } }));
    document.dispatchEvent(new CustomEvent('cw:fold:sync'));
  }

  #onCart = () => {
    if (this.dialog.open) this.#paint();
  };

  /* ---------------- Building ---------------- */

  #build() {
    // Logged out: what Order Again is for, and LOGIN (owner, 2 Oct: a screen, not a popup
    // that ambushes the tap). Signing in comes back here (cw-signin.js 'orderagain').
    if (!document.body.dataset.customer) {
      this.#rows = [];
      this.#tiles = [];
      this.#orders = [];
      this.isNew = true;
      this.bodyEl.innerHTML = `<div class="cw-oa-new cw-oa-new--guest">
        <span class="cw-oa-new__head">Your usuals live here</span>
        <span class="cw-oa-new__text">Log in to see what you buy and order it again in a tap. Your past orders and saved addresses come with you.</span>
        <button type="button" class="cw-oa-new__cta" data-act="signin">LOGIN</button>
      </div>`;
      return;
    }
    const history = read('cw-history') || [];
    const orders = read('cw-oa-orders') || [];
    const counts = read('cw-oa-aisles') || {};
    const due = new Map(usuals().map((u) => [String(u.id), u]));
    this.isNew = orders.length === 0 && history.length === 0;

    const rows = [];
    for (const h of history) {
      const m = productData(h.id);
      if (!m || !m.aisle) continue;
      learn(String(m.id), m.aisle);
      let lines = (h.l || [])
        .map(([id, qty]) => ({ v: m.variants.find((x) => String(x.id) === String(id)), qty: Math.max(1, Number(qty) || 1) }))
        .filter((line) => line.v);
      if (!lines.length) {
        const dv = defaultPack(m) || m.variants[0];
        if (dv) lines = [{ v: dv, qty: 1 }];
      }
      const u = due.get(String(m.id)) || null;
      for (const line of lines) rows.push({ m, usual: line.v, qty: line.qty, h, u, solo: lines.length === 1 });
    }
    const rank = (r) => (r.u?.low ? 0 : r.u?.due ? 1 : r.u ? 2 : 3);
    rows.sort((a, b) => rank(a) - rank(b) || b.h.last - a.h.last);
    const groups = AISLES.map((a) => ({ a, rows: rows.filter((r) => r.m.aisle === a.id) })).filter((g) => g.rows.length);
    this.#rows = groups.flatMap((g) => g.rows);

    // One product per aisle not shopped yet; the squirrel's aisle first. Frozen while open.
    const inBasket = new Set([...cart.productsIn()].map((id) => aisleOf(id)).filter(Boolean));
    const mine = new Set(groups.map((g) => g.a.id));
    const picks = (read('cw-oa-picks') || []).map((id) => productData(id)).filter(Boolean);
    const shelf = load('cw:shelf', {});
    const target = shelf.pending || shelf.target;
    let ids = AISLES.map((a) => a.id).filter((id) => this.isNew || (!inBasket.has(id) && !mine.has(id)));
    if (target && ids.includes(target)) ids = [target, ...ids.filter((id) => id !== target)];
    this.#tiles = [];
    for (const id of ids) {
      const m = picks.find((p) => p.aisle === id);
      const dv = m && defaultPack(m);
      if (!dv) continue;
      learn(String(m.id), m.aisle);
      this.#tiles.push({ id, a: AISLES.find((x) => x.id === id), m, dv, why: whyOf(m.title) });
      if (this.#tiles.length === (this.isNew ? 9 : 4)) break;
    }

    this.#orders = orders.map((o) => {
      const want = new Map();
      for (const l of o.lines) {
        if (l.p && l.al) learn(String(l.p), l.al);
        if (!l.ok || l.mix || !l.v) continue;
        const was = want.get(String(l.v));
        want.set(String(l.v), { v: String(l.v), p: String(l.p), q: (was?.q || 0) + l.q });
      }
      const names = (list) => [...new Set(list.map((l) => shortName(l.t)))].join(', ');
      const note = [
        [o.lines.filter((l) => !l.ok && !l.gone), 'sold out'],
        [o.lines.filter((l) => l.gone), 'no longer sold'],
        [o.lines.filter((l) => l.ok && l.mix), 'not added (your own mix)'],
      ]
        .filter(([list]) => list.length)
        .map(([list, words]) => `${names(list)} ${words}`)
        .join(' · ');
      return { o, lines: [...want.values()], note };
    });

    this.bodyEl.innerHTML = [
      `<div class="cw-oa-far" data-cw="far" hidden><span class="cw-oa-far__text" data-cw="far-text"></span><button type="button" class="cw-oa-far__change" data-act="where">CHANGE</button></div>`,
      this.isNew
        ? `<div class="cw-oa-new"><span class="cw-oa-new__head">Your usuals will show up here</span><span class="cw-oa-new__text">After your first order, you can order them again in a tap.</span></div>`
        : '',
      `<div class="cw-oa-due" data-cw="due" hidden>
        <div class="cw-oa-due__text"><span class="cw-oa-due__head" data-cw="due-head"></span><span class="cw-oa-due__names" data-cw="due-names"></span></div>
        <button type="button" class="cw-oa-due__add" data-act="due-add">ADD</button>
      </div>
      <div class="cw-oa-done" data-cw="due-done" hidden><span class="cw-oa-done__text">Due items added ✓</span><button type="button" class="cw-oa-done__undo" data-act="due-undo">UNDO</button></div>`,
      groups.length
        ? `<div class="cw-oa-usuals"><span>Your usuals</span><span class="cw-oa-usuals__hint"><span class="cw-oa-usuals__ring" aria-hidden="true"></span>Tap to add</span></div>`
        : '',
      groups.map((g) => this.#cardHtml(g, counts[g.a.id] || 0)).join(''),
      this.#tiles.length
        ? `<div class="cw-oa-miss">
            <span class="cw-oa-miss__head">${this.isNew ? 'Start with the basics' : 'You may also want'}</span>
            <div class="cw-oa-tiles">${this.#tiles.map((t, i) => this.#tileHtml(t, i)).join('')}</div>
          </div>`
        : '',
      this.#orders.length ? this.#pastHtml() : '',
    ].join('');

    this.#rows.forEach((r, i) => (r.el = this.bodyEl.querySelector(`[data-row="${i}"]`)));
    this.#tiles.forEach((t, i) => (t.el = this.bodyEl.querySelector(`[data-tile="${i}"]`)));
    this.#orders.forEach((x, i) => (x.el = this.bodyEl.querySelector(`[data-order="${i}"]`)));
    // Where the squirrel stood last; the shelf keeps it current from here (cw:shelf:render).
    this.#onShelf({ detail: { hl: load('cw:shelf:view', {})?.hl } });
  }

  #cardHtml(g, total) {
    let index = this.#rows.indexOf(g.rows[0]);
    const n = g.rows.length;
    const more = total - new Set(g.rows.map((r) => r.m.id)).size;
    return `<section class="cw-oa-card" data-oa-aisle="${esc(g.a.id)}" aria-label="${esc(g.a.name)}">
      <div class="cw-oa-card__head"><img class="cw-oa-card__icon" src="${esc(g.a.icon)}" alt="" width="18" height="18"><span class="cw-oa-card__name">${esc(g.a.name)}</span><span class="cw-oa-card__count">${plural(n, 'item')}</span></div>
      ${g.rows.map((r) => this.#rowHtml(r, index++)).join('')}
      ${more >= 4 ? `<a class="cw-oa-card__all" href="${esc(g.a.url)}"><span>See all ${esc(g.a.name)}</span>${this.#icons.right || '›'}</a>` : ''}
    </section>`;
  }

  #rowHtml(r, i) {
    return `<div class="cw-oa-row" data-row="${i}">
      <button type="button" class="cw-oa-row__tick" data-act="tick">${this.#icons.check || '✓'}</button>
      <span class="cw-oa-row__thumb" style="background-image:url('${esc(r.m.image || '')}')"></span>
      <span class="cw-oa-row__text">
        <span class="cw-oa-row__name">${esc(r.m.title)}</span>
        <button type="button" class="cw-oa-row__cfg" data-act="cfg"><span data-cw="cfg"></span>${this.#icons.down || ''}</button>
        <span class="cw-oa-row__why" data-cw="why"></span>
      </span>
      <span class="cw-oa-row__right">
        <span class="cw-oa-amt"><span class="cw-oa-amt__mrp" data-cw="mrp"></span><span class="cw-oa-amt__price" data-cw="price"></span></span>
        <span class="cw-oa-step" data-cw="step" hidden><button type="button" class="cw-oa-step__pm" data-act="dec">−</button><span class="cw-oa-step__q" data-cw="q"></span><button type="button" class="cw-oa-step__pm" data-act="inc">+</button></span>
      </span>
    </div>`;
  }

  #tileHtml(t, i) {
    const what = `${t.m.title}, ${t.dv.label}`;
    return `<div class="cw-oa-tile" data-tile="${i}" data-oa-aisle="${esc(t.id)}">
      <a class="cw-oa-tile__aisle" href="${esc(t.a.url)}"><img src="${esc(t.a.icon)}" alt="" width="16" height="16"><span>${esc(t.a.short || t.a.name)}</span></a>
      <div class="cw-oa-tile__mid">
        <span class="cw-oa-tile__thumb" style="background-image:url('${esc(t.dv.image || t.m.image || '')}')"></span>
        <span class="cw-oa-tile__text"><span class="cw-oa-tile__name">${esc(t.m.title)}</span><span class="cw-oa-tile__meta">${esc(t.dv.label + (t.why ? ` · ${t.why}` : ''))}</span></span>
      </div>
      <div class="cw-oa-tile__foot">
        <span class="cw-oa-tile__price" data-cw="price"></span>
        <button type="button" class="cw-oa-tile__add" data-act="tile-add" aria-label="Add ${esc(what)}">ADD</button>
        <span class="cw-oa-step" data-cw="step" hidden><button type="button" class="cw-oa-step__pm" data-act="tile-dec" aria-label="Remove one ${esc(what)}">−</button><span class="cw-oa-step__q" data-cw="q"></span><button type="button" class="cw-oa-step__pm" data-act="tile-inc" aria-label="Add one ${esc(what)}">+</button></span>
      </div>
    </div>`;
  }

  #pastHtml() {
    const rows = this.#orders
      .map(({ o, note }, i) => {
        const names = o.lines.map((l) => shortName(l.t));
        const list = names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : '');
        return `<div class="cw-oa-order" data-order="${i}">
          <span class="cw-oa-order__text">
            <span class="cw-oa-order__head">${esc(dayMonth(o.at))} · ${esc(rupees(o.total))}</span>
            <span class="cw-oa-order__names">${esc(list)}</span>
            ${note ? `<span class="cw-oa-order__note">${esc(note)}</span>` : ''}
          </span>
          <button type="button" class="cw-oa-order__btn" data-act="reorder"></button>
        </div>`;
      })
      .join('');
    return `<div class="cw-oa-past">
      <button type="button" class="cw-oa-past__toggle" data-act="past" aria-expanded="false"><span class="cw-oa-past__title">Past orders</span><span class="cw-oa-past__n">${this.#orders.length}</span>${this.#icons.down || ''}</button>
      <div class="cw-oa-past__list" data-cw="past-list" hidden>${rows}</div>
    </div>`;
  }

  /* ---------------- Painting (every basket change) ---------------- */

  #paint() {
    this.#paintDue();
    for (const r of this.#rows) if (r.el) this.#paintRow(r);
    for (const t of this.#tiles) if (t.el) this.#paintTile(t);
    for (const x of this.#orders) if (x.el) this.#paintOrder(x);
  }

  /** What a row shows now: its own line, or the one pack of the product in the basket. */
  #state(r) {
    const { m, usual } = r;
    const held = m.variants.filter((v) => cart.qtyOf(v.id) > 0);
    let own = cart.qtyOf(usual.id) > 0 ? usual : null;
    if (!own && r.solo && held.length === 1) own = held[0];
    const inB = !!own;
    const oos = !inB && !m.variants.some((v) => v.available);
    const usualOut = !inB && !oos && !usual.available;
    const shown = own || usual;
    let why = '';
    let tone = '';
    if (oos) [why, tone] = ['Sold out', 'warn'];
    else if (usualOut) [why, tone] = [`${usual.label || 'This pack'} sold out`, 'warn'];
    else if (r.u?.due) [why, tone] = [r.u.low ? 'Likely running low' : 'Due now', 'due'];
    else if (r.u) why = dueIn(r.u.gap - r.u.since);
    else why = `Last bought ${dayMonth(r.h.last)}`;
    return {
      own,
      inB,
      oos,
      usualOut,
      shown,
      q: inB ? cart.qtyOf(own.id) : r.qty,
      why,
      tone,
      cfg: configOf(m, shown) + (!inB && !oos && r.qty > 1 ? ` × ${r.qty}` : ''),
      muted: !r.u?.due && !inB && !oos,
      multi: held.length > 1,
    };
  }

  #paintRow(r) {
    const s = this.#state(r);
    const el = r.el;
    const $ = (name) => el.querySelector(`[data-cw="${name}"]`);
    el.classList.toggle('is-in', s.inB);
    el.classList.toggle('is-oos', s.oos);
    el.classList.toggle('is-muted', s.muted);
    const tick = el.querySelector('[data-act="tick"]');
    tick.disabled = s.oos;
    tick.setAttribute('aria-pressed', String(s.inB));
    tick.setAttribute('aria-label', s.oos ? `${r.m.title}: sold out` : s.inB ? `Take ${r.m.title} out of the basket` : `Add ${r.m.title}, ${s.cfg}`);
    const cfg = el.querySelector('[data-act="cfg"]');
    cfg.disabled = s.oos;
    cfg.setAttribute('aria-label', `Change pack: ${s.cfg}`);
    $('cfg').textContent = s.cfg;
    const why = $('why');
    why.hidden = s.inB || !s.why;
    why.textContent = s.why;
    why.dataset.tone = s.tone;
    const mrp = s.shown.compare > s.shown.price ? s.shown.compare * s.q : 0;
    const mrpEl = $('mrp');
    mrpEl.hidden = !mrp || s.oos;
    mrpEl.innerHTML = mrp ? `<span class="cw-sr">MRP </span>${esc(rupees(mrp))}` : '';
    $('price').textContent = rupees(s.shown.price * s.q);
    $('step').hidden = !s.inB;
    $('q').textContent = String(s.q);
    const [dec, inc] = el.querySelectorAll('.cw-oa-step__pm');
    dec.setAttribute('aria-label', s.q <= 1 ? `Take ${r.m.title} out of the basket` : `Remove one ${r.m.title}`);
    inc.setAttribute('aria-label', `Add one ${r.m.title}`);
    inc.disabled = !s.multi && s.q >= MAX_PER_LINE;
  }

  #paintTile(t) {
    const q = cart.qtyOf(t.dv.id);
    const inB = q > 0;
    t.el.classList.toggle('is-in', inB);
    t.el.querySelector('[data-cw="price"]').textContent = rupees(t.dv.price * Math.max(1, q));
    t.el.querySelector('[data-act="tile-add"]').hidden = inB;
    t.el.querySelector('[data-cw="step"]').hidden = !inB;
    t.el.querySelector('[data-cw="q"]').textContent = String(q);
    t.el.querySelector('[data-act="tile-inc"]').disabled = q >= MAX_PER_LINE;
  }

  #paintOrder(x) {
    const button = x.el.querySelector('[data-act="reorder"]');
    const added = x.lines.length > 0 && x.lines.every((l) => cart.qtyOf(l.v) >= l.q);
    button.classList.toggle('is-in', added);
    button.textContent = added ? 'In basket ✓' : 'REORDER';
    button.disabled = !x.lines.length;
    button.setAttribute('aria-label', added ? `Order of ${dayMonth(x.o.at)} is in the basket. Open the basket` : `Reorder ${dayMonth(x.o.at)}`);
  }

  #paintDue() {
    const card = this.bodyEl.querySelector('[data-cw="due"]');
    const done = this.bodyEl.querySelector('[data-cw="due-done"]');
    if (!card || !done) return;
    // "Due items added ✓" while everything the shortcut added is still in the basket.
    if (this.#added && !this.#added.variants.every((v) => cart.qtyOf(v) > 0)) this.#added = null;
    done.hidden = !this.#added;
    const items = this.#added ? [] : dueItems();
    this.dueList = items;
    card.hidden = items.length === 0;
    if (!items.length) return;
    card.querySelector('[data-cw="due-head"]').textContent = `Likely running low · ${plural(items.length, 'item')}`;
    card.querySelector('[data-cw="due-names"]').textContent = namesOf(items, { short: true });
  }

  /** Header: "{address} · {when}"; past the delivery edge, the strip says pick up instead. */
  #paintWhere = () => {
    const v = view();
    this.whereEl.textContent = v.label === 'Set location' || !v.known ? v.label : `${v.label} · ${lowerFirst(v.line1)}`;
    const far = this.bodyEl.querySelector('[data-cw="far"]');
    if (!far) return;
    const a = v.answer;
    const out = v.known && a && a.serviceable === false;
    far.hidden = !out;
    if (out) {
      const ready = a.pickup?.when ? `, ready ${lowerFirst(a.pickup.when)}` : '';
      far.querySelector('[data-cw="far-text"]').textContent = `${v.label} is outside our delivery area. Pick up at our shop instead${ready}.`;
    }
  };

  /** The card or tile of the aisle the squirrel is on gets the "next" outline. */
  #onShelf = (event) => {
    const hl = event.detail?.hl || null;
    for (const el of this.bodyEl.querySelectorAll('[data-oa-aisle]')) el.classList.toggle('is-next', el.dataset.oaAisle === hl);
  };

  /** Bubble: scroll to that aisle's card or tile (sideways too) and flash it; else the aisle opens. */
  #onShelfGo = (event) => {
    const el = this.bodyEl.querySelector(`[data-oa-aisle="${CSS.escape(event.detail.aisle)}"]`);
    if (!el || !this.dialog.open) return;
    event.preventDefault();
    const behavior = REDUCED.matches ? 'auto' : 'smooth';
    const row = el.closest('.cw-oa-tiles');
    if (row) row.scrollTo({ left: Math.max(0, el.offsetLeft - 12), behavior });
    const top = (row || el).getBoundingClientRect().top - this.bodyEl.getBoundingClientRect().top + this.bodyEl.scrollTop;
    this.bodyEl.scrollTo({ top: Math.max(0, top - 10), behavior });
    for (const lit of this.bodyEl.querySelectorAll('.is-flash')) lit.classList.remove('is-flash');
    el.classList.add('is-flash');
    clearTimeout(this.#flashTimer);
    this.#flashTimer = setTimeout(() => el.classList.remove('is-flash'), 1400);
  };

  /* ---------------- Taps ---------------- */

  /** The options drawer at this pack (it edits the product's lines like anywhere else). */
  #drawer(m, v, source) {
    if (v) setSel(m, { pack: v.pack, form: v.form, grind: v.grind });
    document.dispatchEvent(new CustomEvent('cw:options:open', { cancelable: true, detail: { productId: m.id, source } }));
  }

  /** When the focused control was hidden by the change, focus the one that took its place. */
  #keepFocus(box, selector) {
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body && !active.closest('[hidden]')) return;
      box.querySelector(selector())?.focus({ preventScroll: true });
    });
  }

  #set(variant, qty, source, productId) {
    if (offline()) return null;
    return cart.setQty(variant, Math.max(0, Math.min(MAX_PER_LINE, qty)), source, { productId });
  }

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const act = target.closest('[data-act]');
    if (!act || !this.contains(act)) return;
    const name = act.dataset.act;

    if (name === 'where') {
      document.dispatchEvent(new CustomEvent('cw:address:open', { cancelable: true, detail: { source: act } }));
      return;
    }
    if (name === 'signin') {
      document.dispatchEvent(new CustomEvent('cw:signin:open', { detail: { then: 'orderagain' } }));
      return;
    }
    if (name === 'due-add') return this.#addDue(act);
    if (name === 'due-undo') return this.#undoDue();
    if (name === 'past') {
      const list = this.bodyEl.querySelector('[data-cw="past-list"]');
      list.hidden = !list.hidden;
      act.setAttribute('aria-expanded', String(!list.hidden));
      act.classList.toggle('is-open', !list.hidden);
      return;
    }

    const rowEl = act.closest('[data-row]');
    if (rowEl) {
      const r = this.#rows[Number(rowEl.dataset.row)];
      const s = this.#state(r);
      const pid = String(r.m.id);
      if (name === 'tick') {
        if (s.oos) return;
        if (s.inB) this.#set(s.own.id, 0, act, pid);
        else if (s.usualOut) this.#drawer(r.m, nearest(r.m, r.usual), act);
        else this.#set(r.usual.id, r.qty, act, pid);
      } else if (name === 'cfg') {
        if (!s.oos) this.#drawer(r.m, s.usualOut ? nearest(r.m, r.usual) : s.shown, act);
      } else if (name === 'inc' || name === 'dec') {
        if (s.multi) this.#drawer(r.m, s.shown, act);
        else if (s.own) this.#set(s.own.id, s.q + (name === 'inc' ? 1 : -1), act, pid);
      }
      // − at 1 takes the stepper away: the keyboard stays on the row's tick.
      this.#keepFocus(rowEl, () => '[data-act="tick"]');
      return;
    }

    const tileEl = act.closest('[data-tile]');
    if (tileEl) {
      const t = this.#tiles[Number(tileEl.dataset.tile)];
      const q = cart.qtyOf(t.dv.id);
      if (name === 'tile-add') this.#set(t.dv.id, 1, act, String(t.m.id));
      else if (name === 'tile-inc') this.#set(t.dv.id, q + 1, act, String(t.m.id));
      else if (name === 'tile-dec') this.#set(t.dv.id, q - 1, act, String(t.m.id));
      // The ADD and the stepper swap places: keep the keyboard on the tile.
      this.#keepFocus(tileEl, () => (cart.qtyOf(t.dv.id) > 0 ? '[data-act="tile-inc"]' : '[data-act="tile-add"]'));
      return;
    }

    const orderEl = act.closest('[data-order]');
    if (orderEl && name === 'reorder') this.#reorder(this.#orders[Number(orderEl.dataset.order)], act);
  };

  /** The screen moves at once (the basket follows, cw-cart.js); UNDO waits for the add to land. */
  #addDue(button) {
    const items = this.dueList || [];
    if (!items.length || offline()) return;
    const run = addDue(items, button);
    this.#added = { variants: items.flatMap((i) => i.lines.map((l) => l.variant)), run };
    this.#paintDue();
    this.bodyEl.querySelector('[data-act="due-undo"]')?.focus({ preventScroll: true });
    document.dispatchEvent(
      new CustomEvent('cw:toast', { detail: { text: `${plural(items.length, 'due item')} added`, undo: () => this.#undoDue() } })
    );
  }

  async #undoDue() {
    const run = this.#added?.run;
    this.#added = null;
    document.dispatchEvent(new CustomEvent('cw:toast', { detail: { hide: true } }));
    if (run) await (await run)();
    this.#paintDue();
  }

  #reorder(x, button) {
    if (!x || !x.lines.length) return;
    if (button.classList.contains('is-in')) {
      document.dispatchEvent(new CustomEvent('cw:basket:open', { cancelable: true, detail: { source: button } }));
      return;
    }
    if (offline()) return;
    const run = addDue(
      x.lines.map((l) => ({ id: l.p, lines: [{ variant: l.v, qty: l.q }] })),
      button
    );
    document.dispatchEvent(
      new CustomEvent('cw:toast', {
        detail: { text: `Order ${x.o.name} added · ${plural(x.lines.length, 'item')}`, undo: async () => (await run)() },
      })
    );
  }
}

if (!customElements.get('cw-order-again')) customElements.define('cw-order-again', CwOrderAgain);
